/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: 功能反馈组件
 * @LastEditors: yongqiang
 * @LastEditTime: 2025-07-26 15:49:25
 */
import React, { useState } from 'react'

import './index.scss'
import { DislikeOutlined, LikeOutlined } from '@ant-design/icons'

// 反馈选项图标查表（iconType 来自 options prop，universe = like/dislike）
const FEEDBACK_ICONS: Record<string, React.ComponentType<any>> = {
  like: LikeOutlined,
  dislike: DislikeOutlined
}
const FeedbackIcon = ({
  name,
  ...rest
}: {
  name: string
  [k: string]: any
}) => {
  const C = FEEDBACK_ICONS[name]
  return C ? <C {...rest} /> : null
}

/**
 * 功能反馈选项接口
 */
interface XCFunctionalFeedbackOption {
  /** 选项文本 */
  text: string
  /** 图标类型 */
  iconType?: string
  /** 点击回调函数 */
  onClick: () => void
  /** 选项类型，用于样式区分
   * positive: 正面反馈
   * negative: 负面反馈
   * default: 默认样式
   */
  type?: 'positive' | 'negative' | 'default'
  /** 选项唯一标识 */
  id?: string
  /** 是否在点击后禁用该按钮 */
  disableAfterClick?: boolean
}

/**
 * 功能反馈组件属性接口
 */
interface XCFunctionalFeedbackProps {
  /** 标题文本 */
  title: string
  /** 反馈选项列表 */
  options: XCFunctionalFeedbackOption[]
}

/**
 * 功能反馈组件
 * @param props 组件属性
 * @returns JSX元素
 */
function FunctionalFeedback({ title, options }: XCFunctionalFeedbackProps) {
  const [disabledButtons, setDisabledButtons] = useState<Set<string>>(new Set())

  const handleOptionClick = (
    option: XCFunctionalFeedbackOption,
    index: number
  ) => {
    const buttonId = option.id || index.toString()

    if (disabledButtons.has(buttonId)) return

    option.onClick()

    if (option.disableAfterClick) {
      setDisabledButtons((prev) => new Set(prev).add(buttonId))
    }
  }

  return (
    <div className="functional-feedback">
      <span className="functional-feedback-label">{title}</span>
      <div className="functional-feedback-buttons">
        {options.map((option, index) => {
          const buttonId = option.id || index.toString()
          const isButtonDisabled = disabledButtons.has(buttonId)

          return (
            <button
              key={buttonId}
              className={`functional-feedback-button ${
                option.type || 'default'
              } ${isButtonDisabled ? 'disabled' : ''}`}
              onClick={() => handleOptionClick(option, index)}
              disabled={isButtonDisabled}
            >
              {option.iconType && (
                <FeedbackIcon
                  name={option.iconType}
                  style={{ marginRight: '4px' }}
                />
              )}
              {option.text}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default FunctionalFeedback
export type { XCFunctionalFeedbackOption, XCFunctionalFeedbackProps }
